/**
 * Runtime bone reference registry.
 *
 * Maintains two distinct concerns:
 *
 * 1. **Bone references** (per-instance) — live THREE.Bone objects for each
 *    mannequin, keyed by object ID.  Used by the gizmo and command system
 *    to manipulate bones at runtime.
 *
 * 2. **Rest poses** (per-model) — immutable bind-pose quaternions loaded
 *    from static data at import time.  These are a property of the MODEL,
 *    never captured from live bones.  Object IDs are mapped to their model
 *    ID so consumers can look up rest poses by object ID alone.
 *
 * This separation makes it impossible for runtime posing to corrupt the
 * rest-pose source of truth — the data is frozen at module load and never
 * derived from mutable THREE.js state.
 */

import type * as THREE from "three/webgpu";
import { MODEL_REST_POSES, type Q4 } from "./restPoseData.ts";

// ---------------------------------------------------------------------------
// Per-instance bone references
// ---------------------------------------------------------------------------

const registry = new Map<string, Map<string, THREE.Bone>>();

// ---------------------------------------------------------------------------
// Object → model mapping  (so getRestPose can resolve by objectId alone)
// ---------------------------------------------------------------------------

const objectModels = new Map<string, string>();

// ---------------------------------------------------------------------------
// Per-model rest poses — pre-populated from static data, immutable
// ---------------------------------------------------------------------------

const modelRestPoses = new Map<string, ReadonlyMap<string, Q4>>(
  Object.entries(MODEL_REST_POSES),
);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register a mannequin's bone references and model association.
 *
 * Called by MannequinRenderer when a cloned scene is created.  The `modelId`
 * maps this instance to its model's immutable rest poses.
 *
 * IMPORTANT: This function never reads bone.quaternion — rest poses come
 * exclusively from the static data in restPoseData.ts.
 */
export function registerBones(
  objectId: string,
  bones: Map<string, THREE.Bone>,
  modelId: string,
): void {
  registry.set(objectId, bones);
  objectModels.set(objectId, modelId);
}

/**
 * Unregister a mannequin instance.
 *
 * Removes the bone references and object→model mapping.  Model-level
 * rest poses are never removed — they are shared across all instances.
 */
export function unregisterBones(objectId: string): void {
  registry.delete(objectId);
  objectModels.delete(objectId);
}

export function getBoneMap(
  objectId: string,
): Map<string, THREE.Bone> | undefined {
  return registry.get(objectId);
}

export function getBone(
  objectId: string,
  boneName: string,
): THREE.Bone | undefined {
  return registry.get(objectId)?.get(boneName);
}

/**
 * Look up a single bone's rest-pose quaternion.
 *
 * Resolution: objectId → modelId → per-model rest poses → boneName.
 */
export function getRestPose(
  objectId: string,
  boneName: string,
): [number, number, number, number] | undefined {
  const modelId = objectModels.get(objectId);
  if (!modelId) return undefined;
  return modelRestPoses.get(modelId)?.get(boneName) as
    | [number, number, number, number]
    | undefined;
}

/**
 * Look up all rest-pose quaternions for a mannequin's model.
 *
 * The returned Map is shared and immutable — do not attempt to mutate it.
 */
export function getRestPoses(
  objectId: string,
): Map<string, [number, number, number, number]> | undefined {
  const modelId = objectModels.get(objectId);
  if (!modelId) return undefined;
  return modelRestPoses.get(modelId) as
    | Map<string, [number, number, number, number]>
    | undefined;
}
